import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import {
  autoSelectedValues,
  CxmPayoutAccountField,
  dependentFields,
  fieldLabel,
  fieldOptions,
} from "src/api";

/**
 * Payout-account form built from CXM's live schema rather than a hardcoded
 * field list. Four rules the gateway forces on us:
 *
 *  - the field name IS the JSON key, spaces and typos included (type 17's
 *    holder-name key ends in a stray `))`), so the label is cleaned only for
 *    display and the key goes back verbatim;
 *  - CXM validates nothing — it stores `{}` and unknown keys as `approved` —
 *    so the checks here are the last ones before a payout account the gateway
 *    cannot read is created;
 *  - a `select` may only carry a value from its own list, and a grouped list
 *    is chosen by the answer to `dependsOn` (country picks the bank, of which
 *    one country has 235);
 *  - a field the user has no choice about is not a question. `source:
 *    "profile"` offers exactly one country, dictated by their residence.
 */
const CxmPayoutAccountForm: React.FC<{
  fields: CxmPayoutAccountField[];
  submitting: boolean;
  error: string | null;
  onSubmit: (details: Record<string, string>) => void;
  onCancel: () => void;
}> = ({ fields, submitting, error, onSubmit, onCancel }): JSX.Element => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Runs again after each pick, because answering a parent can leave its
  // dependent list with a single option too.
  useEffect(() => {
    setValues((prev) => {
      const filled = autoSelectedValues(fields, prev);
      return Object.keys(filled).length ? { ...prev, ...filled } : prev;
    });
  }, [fields, values]);

  const setValue = useCallback(
    (name: string, value: string) =>
      setValues((prev) => {
        // A bank chosen for Poland is not a bank in Brazil: every field that
        // hangs off this one, and anything hanging off those, is cleared —
        // otherwise the stale pick stays in `details` and passes validation
        // against the new parent's list only by accident.
        const cleared = Object.fromEntries(
          dependentFields(fields, name).map((child) => [child, ""]),
        );
        return { ...prev, ...cleared, [name]: value };
      }),
    [fields],
  );

  const missing = (name: string) => touched[name] && !String(values[name] ?? "").trim();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(Object.fromEntries(fields.map((f) => [f.name, true])));
    onSubmit(values);
  };

  const renderField = (field: CxmPayoutAccountField) => {
    const label = fieldLabel(field.name);
    const value = values[field.name] ?? "";

    if (field.kind !== "select") {
      return (
        <Field
          key={field.name}
          label={label}
          value={value}
          onChange={(next) => setValue(field.name, next)}
          onBlur={() => setTouched((prev) => ({ ...prev, [field.name]: true }))}
          error={missing(field.name) ? t("CXM.PAYOUT_ACCOUNT.REQUIRED") : undefined}
          disabled={submitting}
        />
      );
    }

    const options = fieldOptions(field, values);
    const parentLabel = field.dependsOn ? fieldLabel(field.dependsOn) : "";
    // An empty grouped list means the parent has not been answered yet. Say
    // which one, rather than offering a dropdown that opens onto nothing.
    const waiting = options.length === 0 && Boolean(field.dependsOn);

    return (
      <div className="cxm-payout-select" key={field.name}>
        <DropdownAnimation
          wrapperClass="cxm-payout-select__control"
          label={label}
          selectedValue={value || null}
          options={options.map((o) => ({ key: o.value, value: o.label }))}
          placeholder={
            waiting
              ? t("CXM.PAYOUT_ACCOUNT.SELECT_AFTER", { field: parentLabel })
              : t("CXM.PAYOUT_ACCOUNT.SELECT_PLACEHOLDER")
          }
          onChange={(option) => {
            setTouched((prev) => ({ ...prev, [field.name]: true }));
            setValue(field.name, option.key);
          }}
          disabled={submitting || waiting || options.length === 0}
          hideArrow={waiting}
        />
        {missing(field.name) && (
          <p className="cxm-payout-select__error">{t("CXM.PAYOUT_ACCOUNT.REQUIRED")}</p>
        )}
      </div>
    );
  };

  // A select whose only option was filled in for the user is not shown: it
  // would be a dropdown that cannot be changed.
  const asked = useMemo(
    () =>
      fields.filter(
        (f) =>
          !(
            f.kind === "select" &&
            String(values[f.name] ?? "").trim() &&
            fieldOptions(f, values).length === 1
          ),
      ),
    [fields, values],
  );

  return (
    <form className="cxm-payout-account-form" onSubmit={handleSubmit}>
      <h3 className="cxm-payout-account-form__title">{t("CXM.PAYOUT_ACCOUNT.TITLE")}</h3>
      <p className="cxm-payout-account-form__hint">{t("CXM.PAYOUT_ACCOUNT.HINT")}</p>

      {asked.map(renderField)}

      <ErrorAlert message={error} />

      <Button
        title={t("CXM.PAYOUT_ACCOUNT.SAVE")}
        className="cxm-button"
        type="submit"
        pending={submitting}
      />
      <Button
        title={t("CXM.PAYOUT_ACCOUNT.CANCEL")}
        className="cxm-button"
        type="button"
        secondary
        onClick={onCancel}
        disabled={submitting}
      />
    </form>
  );
};

export default CxmPayoutAccountForm;
